
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
}

export function adjustBrightness(hex: string, percent: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const adjust = (color: number) => {
    const adjusted = color + (color * percent / 100);
    return Math.max(0, Math.min(255, Math.round(adjusted)));
  };
  
  const r = adjust(rgb.r).toString(16).padStart(2, '0');
  const g = adjust(rgb.g).toString(16).padStart(2, '0');
  const b = adjust(rgb.b).toString(16).padStart(2, '0');
  
  return `#${r}${g}${b}`;
}

export function adjustOpacity(hex: string, opacity: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
}

export function getContrastText(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#000000';
  
  // Calculate relative luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  
  // Return white text for dark backgrounds, black text for light backgrounds
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

export function getContrastTextHSL(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '0 0% 0%';
  
  // Calculate relative luminance
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  
  // Return white text for dark backgrounds, black text for light backgrounds in HSL
  return luminance > 0.5 ? '0 0% 0%' : '0 0% 100%';
}

export function getMutedForegroundHSL(background: string, foreground: string): string {
  const bgRgb = hexToRgb(background);
  const fgRgb = hexToRgb(foreground);
  
  if (!bgRgb || !fgRgb) {
    return '215 25% 55%'; // fallback muted color
  }
  
  // Calculate background luminance
  const bgLuminance = (0.299 * bgRgb.r + 0.587 * bgRgb.g + 0.114 * bgRgb.b) / 255;
  
  // If background is light, make muted text darker but not as dark as foreground
  // If background is dark, make muted text lighter but not as light as foreground
  if (bgLuminance > 0.5) {
    // Light background - use a medium gray
    return '215 25% 55%'; // HSL for a nice medium gray
  } else {
    // Dark background - use a lighter gray
    return '215 20% 70%'; // HSL for a lighter gray
  }
}

export function hexToHSL(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '0 0% 0%';
  
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
